import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Phone, MapPin } from "lucide-react";
import { waLink, PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";

export const AGENCY_LINKS = [
  { id: "services", label: "Services" },
  { id: "industries", label: "Industries" },
  { id: "packages", label: "Packages" },
  { id: "portfolio", label: "Work" },
  { id: "audit", label: "Free Audit" },
  { id: "faq", label: "FAQ" },
];

export const agencyWa = (topic) =>
  waLink(`Hi LaunchX Agency, ${topic}`);

export const AgencyNav = ({ scrollTo }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <header data-testid="agency-nav" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-navy/95 backdrop-blur-xl shadow-lg shadow-navy/30" : "bg-navy/80 backdrop-blur-md"} border-b border-white/5`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/agency" data-testid="agency-nav-logo" className="flex items-center gap-2.5 group">
          <img src="/logo-mark.png" alt="LaunchX Agency" className="h-9 w-auto group-hover:scale-[1.04] transition-transform" />
          <span className="font-display font-bold text-white text-lg leading-none">LaunchX <span className="text-cyanx">Agency</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {AGENCY_LINKS.map((l) => (
            <button key={l.id} data-testid={`agency-nav-link-${l.id}`} onClick={() => go(l.id)} className="text-[12.5px] font-medium text-slate-300 hover:text-cyanx transition-colors tracking-wide">
              {l.label}
            </button>
          ))}
          <Link to="/" data-testid="agency-nav-academy-link" className="text-[12.5px] font-semibold text-slate-400 hover:text-white transition-colors tracking-wide">
            LaunchX Academy
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a data-testid="agency-nav-whatsapp" href={agencyWa("I want to discuss digital marketing for my business.")} target="_blank" rel="noreferrer"
            className="text-[13px] font-semibold text-slate-300 hover:text-white border border-white/15 hover:border-cyanx/50 rounded-full px-5 py-2.5 transition-colors">
            WhatsApp Us
          </a>
          <button data-testid="agency-nav-book-button" onClick={() => go("consultation")}
            className="text-[13px] font-semibold text-white bg-electric hover:bg-electric-dark rounded-full px-5 py-2.5 transition-all hover:shadow-lg hover:shadow-electric/40 hover:-translate-y-0.5">
            Free Consultation
          </button>
        </div>

        <button data-testid="agency-nav-mobile-menu-button" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav data-testid="agency-nav-mobile-drawer" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="lg:hidden overflow-hidden bg-navy border-t border-white/10">
            <div className="px-6 py-6 flex flex-col gap-1">
              {AGENCY_LINKS.map((l, i) => (
                <motion.button key={l.id} data-testid={`agency-nav-mobile-link-${l.id}`} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} onClick={() => go(l.id)} className="text-left font-display text-lg font-semibold text-slate-200 hover:text-cyanx py-2.5 transition-colors">
                  {l.label}
                </motion.button>
              ))}
              <button data-testid="agency-nav-mobile-book-button" onClick={() => go("consultation")} className="mt-4 text-sm font-semibold text-white bg-electric rounded-full px-6 py-3.5 text-center">
                Book Free Consultation
              </button>
              <Link to="/" data-testid="agency-nav-mobile-academy-link" className="mt-2 text-sm font-semibold text-cyanx border border-cyanx/40 rounded-full px-6 py-3.5 text-center">
                LaunchX Academy
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export const AgencyFooter = () => (
  <footer data-testid="agency-footer" className="bg-[#070C16] border-t border-white/5 pt-16 pb-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="LaunchX Agency" className="h-9 w-auto" data-testid="agency-footer-logo" />
            <span className="font-display font-bold text-white text-lg">LaunchX <span className="text-cyanx">Agency</span></span>
          </div>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">Digital marketing that brings local businesses real enquiries — websites, Google visibility and ads that pay for themselves.</p>
          <p className="mt-6 flex items-start gap-2 text-sm text-slate-500">
            <MapPin size={15} className="mt-0.5 shrink-0 text-electric" /> Rampur, Uttar Pradesh — serving businesses across India
          </p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Explore</p>
          <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3">
            <Link to="/agency" data-testid="agency-footer-link-home" className="text-sm text-slate-400 hover:text-cyanx transition-colors">Agency Home</Link>
            <Link to="/" data-testid="agency-footer-link-academy" className="text-sm text-slate-400 hover:text-cyanx transition-colors">LaunchX Academy</Link>
            <Link to="/scholarship" data-testid="agency-footer-link-scholarship" className="text-sm text-slate-400 hover:text-cyanx transition-colors">Scholarship Test</Link>
            <Link to="/privacy-policy" data-testid="agency-footer-link-privacy" className="text-sm text-slate-400 hover:text-cyanx transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" data-testid="agency-footer-link-terms" className="text-sm text-slate-400 hover:text-cyanx transition-colors">Terms &amp; Conditions</Link>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Talk To Us</p>
          <div className="mt-5 space-y-3">
            <a data-testid="agency-footer-whatsapp" href={agencyWa("I want to grow my business online.")} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-cyanx transition-colors">
              <MessageCircle size={15} className="text-electric" /> WhatsApp: {PHONE_DISPLAY}
            </a>
            <a data-testid="agency-footer-phone" href={`tel:${PHONE_TEL}`} className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-cyanx transition-colors">
              <Phone size={15} className="text-electric" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
      <div className="mt-14 pt-7 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-4">
        <p className="text-xs text-slate-600">© {new Date().getFullYear()} LaunchX Agency, Rampur, Uttar Pradesh. All rights reserved.</p>
        <p className="text-xs text-slate-600 max-w-xl">LaunchX Agency provides digital marketing services. Advertising results depend on industry, budget and competition; specific rankings or returns are never guaranteed.</p>
      </div>
    </div>
  </footer>
);

export const AgencyFloating = () => (
  <a data-testid="agency-floating-whatsapp" href={agencyWa("I want to grow my business online.")} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp"
    className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-[#25D366]/40 flex items-center justify-center hover:scale-110 transition-transform">
    <MessageCircle size={26} className="text-white" fill="white" />
  </a>
);
